import {AmbientLight, DirectionalLight, Fog, MathUtils, Matrix4, Mesh, PerspectiveCamera, Scene, SRGBColorSpace, Vector3, WebGLRenderer} from 'three';
import {getAmbientLight, getDirectionalLight, getFogColor, getFogNearFar} from '../helpers/helpers-mapbox';

const SQRT3 = Math.sqrt(3);

// Base light intensities for this layer, applied at full luminance. They are
// scaled by the relative luminance (0-1) of the corresponding Mapbox light so
// that the lights dim and brighten together with the map.
const DIRECTIONAL_INTENSITY = 3.8,
    AMBIENT_INTENSITY = 0.4;

// Sets a THREE.Color from an [r, g, b] color with 0-255 sRGB components,
// decoding it to the renderer's linear working color space.
function setSRGBColor(target, [r, g, b]) {
    target.setRGB(r / 255, g / 255, b / 255, SRGBColorSpace);
}

export default class {

    constructor(implementation) {
        const me = this;

        me.implementation = implementation;
        me._onResize = me._onResize.bind(me);
        me._updateLights = me._updateLights.bind(me);
    }

    onAdd(map, beforeId) {
        const me = this,
            implementation = me.implementation,
            id = implementation.id,
            _mbox = map.map;

        me.map = map;
        me.modelOrigin = map.getModelOrigin();

        _mbox.addLayer({
            id,
            type: 'custom',
            renderingMode: '3d',
            onAdd: (mbox, gl) => {
                me._onAdd(mbox, gl);
                if (implementation.onAdd) {
                    implementation.onAdd(map, {
                        renderer: me.renderer,
                        scene: me.scene,
                        camera: me.camera
                    });
                }
            },
            onRemove: (mbox, gl) => {
                me._onRemove(mbox, gl);
                if (implementation.onRemove) {
                    implementation.onRemove(map, {
                        renderer: me.renderer,
                        scene: me.scene,
                        camera: me.camera
                    });
                }
            },
            prerender: () => {
                if (implementation.prerender) {
                    implementation.prerender(map, {
                        renderer: me.renderer,
                        scene: me.scene,
                        camera: me.camera
                    });
                }
            },
            render: me._render.bind(me)
        }, beforeId || 'poi');
        _mbox.setLayerZoomRange(id, implementation.minzoom, implementation.maxzoom);
    }

    _onAdd(mbox, gl) {
        const me = this,
            {fov, width, height} = mbox.transform,   // MapLibre 의 fov 는 도 단위다
            renderer = me.renderer = new WebGLRenderer({
                canvas: mbox.getCanvas(),
                context: gl
            }),
            scene = me.scene = new Scene(),
            lightColor = me.implementation.lightColor,
            light = me.light = new DirectionalLight(lightColor, DIRECTIONAL_INTENSITY),
            ambientLight = me.ambientLight = new AmbientLight(lightColor, AMBIENT_INTENSITY);

        renderer.autoClear = false;

        scene.add(light);
        scene.add(ambientLight);
        scene.fog = new Fog(0x000000);

        // This is needed to avoid a black screen with empty scene
        scene.add(new Mesh());

        me.mbox = mbox;
        me.camera = new PerspectiveCamera(fov, width / height);

        // The view matrix is set directly in _render(), so prevent the renderer
        // from recomputing matrixWorld/matrixWorldInverse from the camera's
        // position/quaternion/scale (see _render() for details).
        me.camera.matrixWorldAutoUpdate = false;

        mbox.on('resize', me._onResize);

        if (lightColor === undefined) {
            me.map.on('light', me._updateLights);
            me._updateLights({directional: getDirectionalLight(mbox), ambient: getAmbientLight(mbox)});
        }
    }

    _onRemove(mbox) {
        const me = this;

        mbox.off('resize', me._onResize);
        if (me.implementation.lightColor === undefined) {
            me.map.off('light', me._updateLights);
        }
        delete me.mbox;
    }

    // MapLibre v5 는 두 번째 인자로 **객체**를 준다 (Mapbox 는 행렬 배열).
    // `Matrix4().fromArray(객체)` 는 에러 없이 NaN 행렬이 되고, 그러면 아무것도
    // 안 그려지면서 콘솔은 조용하다 - 2026-09-07 이관 실패의 원인이 이것이었다.
    //
    // 그리고 손으로 만들던 투영행렬(makePerspective + _horizonShift 상수)을
    // 버렸다. MapLibre 가 `projectionMatrix`/`nearZ`/`farZ` 를 직접 주므로
    // 재현할 이유가 없다.
    _render(gl, options) {
        const me = this,
            {modelOrigin, mbox, renderer, camera, light, scene} = me,
            {modelViewProjectionMatrix, projectionMatrix, nearZ, farZ} = options,
            {pitch, cameraToCenterDistance, worldSize, pixelsPerMeter} = mbox.transform,
            cameraToSeaLevelDistance = cameraToCenterDistance / Math.cos(MathUtils.degToRad(pitch)),

            m = new Matrix4().fromArray(modelViewProjectionMatrix),

            // ⚠️ **MapLibre 의 MVP 는 x·y 는 픽셀, z 는 미터를 받는다.**
            // Mapbox 는 커스텀 레이어에 머케이터[0,1] 행렬(z 도 같은 배율)을 준다.
            // MapLibre 소스(`_calcMatrices`)가 축마다 다르게 스케일한다:
            //   mercatorMatrix = scale(m, [worldSize, worldSize, worldSize])
            //   scale(m, m, [1, 1, pixelPerMeter]);  viewProjMatrix = m
            // 즉 x·y 는 머케이터×worldSize, **z 는 미터**다.
            //
            // 씬 좌표는 세 축 모두 머케이터이므로 z 만 미터로 되돌린다.
            // `worldSize / pixelsPerMeter` = 1 / (미터당 머케이터) 이고
            // 이 값은 **줌과 무관하다**(둘이 같은 배율로 움직인다).
            //
            // 안 맞으면 화면 밖으로 날아가거나(x·y) 높이만 부푼다(z) - 둘 다
            // 에러가 없다. 실측: z 를 worldSize 로 곱했을 때 zoom 18 에서
            // 높이가 pixelsPerMeter(=4.23)배로 부풀어 버스가 탑처럼 섰다.
            // zoom 16 에서는 1.06 배라 눈에 안 보였다.
            l = new Matrix4()
                .makeScale(worldSize, worldSize, worldSize / pixelsPerMeter)
                .multiply(new Matrix4().makeTranslation(modelOrigin.x, modelOrigin.y, 0))
                .scale(new Vector3(1, -1, 1));

        camera.near = nearZ;
        camera.far = farZ;
        camera.projectionMatrix.fromArray(projectionMatrix);
        camera.projectionMatrixInverse.copy(camera.projectionMatrix).invert();

        // Set the view matrix directly so that projectionMatrix * matrixWorldInverse
        // reproduces the map's view-projection matrix (m * l). Previously this was done
        // by decomposing the camera world matrix into position/quaternion/scale and
        // letting the renderer rebuild matrixWorldInverse from them. Since three.js
        // r183, Camera.updateMatrixWorld() strips the scale component from
        // matrixWorldInverse ("exclude scale from view matrix to be glTF conform"), but
        // here the decomposed scale is non-identity and essential, so the projection
        // broke and every object became invisible. matrixWorldAutoUpdate is disabled in
        // _onAdd() so the renderer keeps the matrices set here.
        camera.matrixWorldInverse.copy(camera.projectionMatrixInverse).multiply(m).multiply(l);
        camera.matrixWorld.copy(camera.matrixWorldInverse).invert();

        const rad = MathUtils.degToRad(mbox.getBearing() + 30);
        light.position.set(-Math.sin(rad), -Math.cos(rad), SQRT3).normalize();

        // The light color/intensity is updated on the map's 'light' event; only the
        // fog is refreshed per frame here. Anchor the fog to the screen-center
        // (cameraToSeaLevelDistance) and far plane (farZ) depths, so distant
        // geometry fades into the fog color like the map.
        setSRGBColor(scene.fog.color, getFogColor(mbox));
        [scene.fog.near, scene.fog.far] = getFogNearFar(cameraToSeaLevelDistance, farZ);

        renderer.resetState();

        // When the implementation provides its own render(), delegate the final
        // draw to it so it can render to offscreen targets and run post-processing
        // (e.g. bloom) before compositing onto the map. The camera, fog and lights
        // are already configured above. Falls back to the default scene render.
        const implementation = me.implementation;
        if (implementation.render) {
            implementation.render(me.map, {renderer, scene, camera});
        } else {
            renderer.render(scene, camera);
        }
    }

    // Applies the given directional and ambient light (from the map's 'light' event
    // payload). Called on the event and once on add; only used when lightColor is
    // undefined.
    _updateLights({directional, ambient}) {
        const {light, ambientLight} = this;

        setSRGBColor(light.color, directional.color);
        light.intensity = directional.intensity * DIRECTIONAL_INTENSITY;
        setSRGBColor(ambientLight.color, ambient.color);
        ambientLight.intensity = ambient.intensity * AMBIENT_INTENSITY;
    }

    _onResize(event) {
        const camera = this.camera,
            transform = event.target.transform;

        camera.aspect = transform.width / transform.height;
        camera.updateProjectionMatrix();
    }

}
